import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface CheckoutResultBannerProps {
    /**
     * True when Stripe is running on test keys. Drives the success copy only —
     * a test charge must not be announced as a real one, and vice versa.
     */
    testMode: boolean;
}

/**
 * Reads `?checkout=success|cancelled` from /pricing and shows a dismissible
 * banner. URL parameter is the source of truth for now (no webhook wiring in
 * the test/MVP), so this is sufficient for a "did the charge land?"
 * confirmation. Webhook-driven server-side state is a follow-up story.
 */
const CheckoutResultBanner: React.FC<CheckoutResultBannerProps> = ({ testMode }) => {
    const [params, setParams] = useSearchParams();
    const result = params.get('checkout');
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        setDismissed(false);
    }, [result]);

    if (dismissed || (result !== 'success' && result !== 'cancelled')) {
        return null;
    }

    const isSuccess = result === 'success';
    const successMessage = testMode
        ? 'Test payment received — thanks for trying CGM Sports! No production access has been provisioned yet (this is a test charge).'
        : 'Payment received — welcome to CGM Sports! Your annual subscription is active.';
    const message = isSuccess
        ? successMessage
        : 'Checkout cancelled — no charges were made. Subscribe whenever you are ready.';
    const accent = isSuccess ? '#10b981' : '#f59e0b';

    const handleDismiss = () => {
        const next = new URLSearchParams(params);
        next.delete('checkout');
        setParams(next, { replace: true });
        setDismissed(true);
    };

    return (
        <div
            role="alert"
            aria-live="assertive"
            className="fixed top-20 left-1/2 -translate-x-1/2 z-30 max-w-2xl w-[92%] px-4 py-3 bg-neutral-900 text-white border flex items-start gap-3 shadow-xl"
            style={{ borderColor: accent }}
        >
            <span
                aria-hidden="true"
                className="mt-0.5 inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: accent }}
            />
            <p className="text-sm leading-snug flex-1">{message}</p>
            <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss notification"
                className="text-gray-400 hover:text-white transition-colors text-lg leading-none cursor-pointer"
            >
                ×
            </button>
        </div>
    );
};

export default CheckoutResultBanner;
